import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

// Function to check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Function to get valid token
export const getValidToken = () => {
  const token = Cookies.get('accessToken');
  if (!token || isTokenExpired(token)) {
    return null;
  }
  return token;
};

// Function to clear authentication data
export const clearAuthData = () => {
  Cookies.remove('accessToken');
  localStorage.removeItem('email');
  localStorage.removeItem('role');
  localStorage.removeItem('id');
  localStorage.removeItem('storeName');
  localStorage.removeItem('storeCode');
  localStorage.removeItem('tableauToken');
};

// Function to store authentication data (for both regular and Google login)
export const storeAuthData = (authData) => {
  if (authData.accessToken) {
    Cookies.set('accessToken', authData.accessToken, { expires: 7 });
  }
  if (authData.email) {
    localStorage.setItem('email', authData.email);
  }
  if (authData.role) {
    localStorage.setItem('role', authData.role);
  }
  if (authData.id) {
    localStorage.setItem('id', authData.id);
  }
  if (authData.storeName) {
    localStorage.setItem('storeName', authData.storeName);
  }
  if (authData.storeCode) {
    localStorage.setItem('storeCode', authData.storeCode);
  }
  if (authData.tableauToken) {
    localStorage.setItem('tableauToken', authData.tableauToken);
  }
};

// Function to handle successful authentication (for both regular and Google login)
export const handleSuccessfulAuth = async (authData, navigate, toast) => {
  try {
    // Store authentication data
    storeAuthData(authData);

    // Generate Tableau JWT token if possible
    try {
      const resJWTTableau = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/generatejwt`,
        { withCredentials: true, timeout: 5000 }
      );
      if (resJWTTableau.data) {
        localStorage.setItem('tableauToken', resJWTTableau.data);
      }
    } catch (err) {
      console.warn('Failed to generate Tableau token:', err);
      // Don't block login for Tableau token failure
    }

    // Show success message
    if (toast) {
      toast({
        title: 'Login Successful',
        description: 'Welcome back!',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }

    // Navigate to homepage via full page reload to ensure App.js re-evaluates auth state
    window.location.href = '/homepage';

    return true;
  } catch (error) {
    console.error('Error in handleSuccessfulAuth:', error);
    if (toast) {
      toast({
        title: 'Warning',
        description: 'Login successful, but some features may be limited.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }

    // Still navigate even if Tableau token fails
    window.location.href = '/homepage';
    
    return false;
  }
};

// Function to handle authentication error
export const handleAuthError = (navigate, toast) => {
  clearAuthData();
  
  if (toast) {
    toast({
      title: "Authentication Error",
      description: "Your session has expired. Please log in again.",
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  }
  
  if (navigate) {
    navigate('/');
  } else {
    window.location.href = '/';
  }
};

// Axios response interceptor to handle 401 errors globally
export const setupAxiosInterceptors = (navigate, toast) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        handleAuthError(navigate, toast);
      }
      return Promise.reject(error);
    }
  );
};

// Hook for checking authentication status
export const useAuthCheck = () => {
  const token = getValidToken();
  return {
    isAuthenticated: !!token,
    token,
    decodedToken: token ? jwtDecode(token) : null,
  };
};
